<template>
	<RotationLayout :rotation="rotation" :subtitle="trans('rotations.elections.documentation.compromise_letters.title')">
		<Empty v-if="compromiseLetters.length === 0"/>
		<CardGrid v-else>
			<template v-for="compromiseLetter in compromiseLetters" :key="`compromise-letter-${compromiseLetter.id}`">
				<Card :title="compromiseLetter.candidate.user.full_name" :subtitle="dayjs(compromiseLetter.created_at).format(trans('general.dayjs.datetime'))">
					<template #badges>
						<Badge :color="compromiseLetter.is_accepted ? Color.Green : compromiseLetter.is_rejected ? Color.Red : Color.Orange" active>{{ trans(`rotations.elections.documentation.meeting_letters.status.${compromiseLetter.status}`) }}</Badge>
					</template>
					<LabeledObject v-if="compromiseLetter.is_pending" full>
						<template #label>{{ trans('general.rejection_reason') }}</template>
						<textarea v-model="rejectionReason"/>
					</LabeledObject>
					<LabeledObject v-else-if="compromiseLetter.is_rejected" full>
						<template #label>{{ trans('general.rejection_reason') }}</template>
						<p>{{ compromiseLetter.rejection_reason }}</p>
					</LabeledObject>
					<template #footer>
						<template v-if="compromiseLetter.is_pending">
							<PrimaryButton :color="Color.Green" @click.stop="acceptCompromiseLetter(compromiseLetter)">
								<i class="fa-solid fa-check mr-2"/>
								{{ trans('admin.periods.requests.actions.accept') }}
							</PrimaryButton>
							<PrimaryButton :color="Color.Red" @click.stop="rejectCompromiseLetter(compromiseLetter)">
								<i class="fa-solid fa-ban mr-2"/>
								{{ trans('admin.periods.requests.actions.reject') }}
							</PrimaryButton>
						</template>
						<a :href="route('association.representatives.rotations.compromise-letters.show', {rotation: rotation.id, compromiseLetter: compromiseLetter.id})" target="_blank">
							<SecondaryButton :color="Color.Blue">
								<i class="fa-solid fa-eye mr-2"/>
								{{ trans('general.view') }}
							</SecondaryButton>
						</a>
					</template>
				</Card>
			</template>
		</CardGrid>
	</RotationLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import {defineProps, type Ref, ref} from 'vue'
import Card from '@/Components/UI/Cards/Card.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import RotationLayout from '@/Layouts/RotationLayout.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {type Rotation} from '@/Types/Periods/Rotations/Rotation'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {type CompromiseLetter} from '@/Types/Periods/Rotations/Documentation/CompromiseLetter'

const props = defineProps<{
	rotation:Rotation,
	compromiseLetters:Array<CompromiseLetter>,
}>()

const rejectionReason = ref('')
const working:Ref<boolean> = ref(false)
const rotation:Ref<Rotation> = ref(props.rotation)
const compromiseLetters:Ref<Array<CompromiseLetter>> = ref(props.compromiseLetters)

function acceptCompromiseLetter(compromiseLetter:CompromiseLetter):void{
	if(!working.value && compromiseLetter.is_pending && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.representatives.rotations.compromise-letters.update', {
			rotation: rotation.value.id,
			compromiseLetter: compromiseLetter.id
		}), {
			action: 'accept',
		}).then(({data}) => {
			compromiseLetter.status = data.status
			compromiseLetter.is_pending = data.is_pending
			compromiseLetter.is_rejected = data.is_rejected
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function rejectCompromiseLetter(compromiseLetter:CompromiseLetter):void{
	if(!working.value && compromiseLetter.is_pending && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.representatives.rotations.compromise-letters.update', {
			rotation: rotation.value.id,
			compromiseLetter: compromiseLetter.id
		}), {
			action: 'reject',
			rejection_reason: rejectionReason.value,
		}).then(({data}) => {
			compromiseLetter.status = data.status
			compromiseLetter.is_pending = data.is_pending
			compromiseLetter.is_rejected = data.is_rejected
			compromiseLetter.rejection_reason = data.rejection_reason
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>